import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  Briefcase,
  RocketLaunch,
  ArrowRight,
  Compass,
  Target,
  MagnifyingGlass,
  UsersThree,
  Buildings,
  Path,
  Chats,
  Calendar,
  CaretDown,
  ChalkboardTeacher,
  GraduationCap,
  Sparkle,
  Lightning,
} from "@phosphor-icons/react";
import { Reveal } from "../lib/motion";
import { Eyebrow, PrimaryButton, GhostLink } from "../components/ui-kit";

const PATHWAYS = [
  {
    tag: "BUILD",
    icon: Lightbulb,
    title: "360° Entrepreneurship Development",
    desc: "For students who want to develop entrepreneurial thinking, identify opportunities, create solutions and learn how ventures are built.",
    anchor: "build",
  },
  {
    tag: "WORK",
    icon: Briefcase,
    title: "Corporate Readiness Lab",
    desc: "For students preparing to transition from campus into professional life and develop the capabilities required to navigate the workplace effectively.",
    anchor: "work",
  },
  {
    tag: "VENTURE",
    icon: RocketLaunch,
    title: "Startup Mentorship & Incubation Support",
    desc: "For students already building startups, student founders and ventures supported by college/university incubation centres.",
    anchor: "venture",
  },
];

const BUILD_JOURNEY = ["Self", "Problem", "Opportunity", "Customer", "Solution", "Business", "Validation", "Market", "Money", "Scale"];

const BUILD_DOMAINS = [
  { n: "01", t: "Entrepreneurial Mindset & Self-Leadership", d: "Initiative, ownership, resilience, calculated risk-taking, ambiguity tolerance, learning agility and entrepreneurial thinking." },
  { n: "02", t: "Opportunity & Problem Intelligence", d: "Identifying meaningful problems, unmet needs, opportunity signals and distinguishing ideas from genuine opportunities." },
  { n: "03", t: "Creativity, Innovation & Design Thinking", d: "Creative thinking, ideation, innovation, design thinking, experimentation and solution development." },
  { n: "04", t: "Customer & Market Intelligence", d: "Customer understanding, segmentation, needs, competition, market dynamics and opportunity assessment." },
  { n: "05", t: "Business Model & Value Creation", d: "Value proposition, customers, channels, revenue, costs, partnerships and business-model thinking." },
  { n: "06", t: "Product / Solution Development & Validation", d: "Prototype, MVP thinking, experimentation, testing assumptions, customer evidence and iteration." },
  { n: "07", t: "Go-to-Market, Sales & Growth", d: "Customer acquisition, sales, distribution, pricing, repeat business and growth." },
  { n: "08", t: "Financial & Commercial Intelligence", d: "Revenue, costs, margins, cash flow, pricing, break-even, unit economics and funding requirements." },
  { n: "09", t: "Venture Building, Leadership & Execution", d: "Team building, decision-making, resource allocation, execution, systems, accountability and founder effectiveness." },
  { n: "10", t: "Pitch, Funding, Scale & Entrepreneurial Leadership", d: "Storytelling, pitching, investor readiness, fundraising fundamentals, scaling, governance, ethics and entrepreneurial leadership." },
];

const CRL_LEVELS = [
  { n: "01", t: "Enter the Corporate World", q: "\u201CI need to understand how this world works.\u201D", d: "Mindset shift, corporate context, values, etiquette, communication and workplace writing." },
  { n: "02", t: "Become Effective at Work", q: "\u201CI need to become good at my job.\u201D", d: "Time and priorities, role expectations, manager effectiveness, problem solving, feedback and collaboration." },
  { n: "03", t: "Navigate the Organisation", q: "\u201CI need to work effectively with people.\u201D", d: "Conflict, stakeholders, negotiation, assertiveness, ambiguity, visibility, networking and personal brand." },
  { n: "04", t: "Think Like a Professional", q: "\u201CI need to create impact and build my career.\u201D", d: "Business and customer thinking, influence, leadership, delegation, career navigation and AI-enabled work." },
];

const REAL_METHOD = [
  { l: "R", w: "Role / Reality", d: "Put students into a realistic workplace situation." },
  { l: "E", w: "Experience", d: "Let them make a choice, communicate, solve, prioritise or respond." },
  { l: "A", w: "Analyse", d: "Reflect on what happened, what worked and what could go wrong." },
  { l: "L", w: "Learn & Apply", d: "Introduce the framework / tool and practise it again." },
];

const REAL_FLOW = ["Hook", "Experience", "Reflection", "Concept", "Practice", "Debrief", "Real-World Takeaway"];

const CRL_CAPABILITIES = [
  "College Corporate: The Mindset Shift", "Understanding the Corporate World", "Values & Professional Mindset",
  "Corporate Etiquette 101", "Corporate Communication — The Basics", "Writing at Work",
  "Time & Priority Management", "Understanding Your Role & Expectations", "Working With Your Manager",
  "Problem Solving & Decision Making", "Feedback", "Collaboration",
  "Conflict Management", "Stakeholder Management", "Negotiation & Assertiveness",
  "Managing Ambiguity", "Visibility, Networking & Personal Brand", "Business & Customer Thinking",
  "Influence & Leadership Without Authority", "Delegation, Ownership & Leadership", "Career Navigation",
  "The AI-Enabled Professional",
];

const VENTURE_JOURNEY = ["Problem", "Customer", "Business Model", "GTM", "Sales", "Revenue", "Unit Economics", "Systems", "Pitch", "Funding", "Execution"];

const VENTURE_AREAS = [
  "Problem & Opportunity Clarity", "Customer Understanding", "Business Model", "Go-to-Market",
  "Sales", "Customer Acquisition", "Revenue Planning", "Unit Economics",
  "Business Systems", "Team Structure", "Founder Effectiveness", "Pitch Preparation",
  "Investor Readiness", "90-Day Execution",
];

const AUDIENCE = [
  { icon: GraduationCap, t: "Colleges & Universities", d: "Entrepreneurship and career-readiness programmes integrated with institutional requirements." },
  { icon: Briefcase, t: "Placement & Career Development Centres", d: "Corporate readiness and campus-to-corporate programmes." },
  { icon: Sparkle, t: "Innovation & Entrepreneurship Cells", d: "Entrepreneurship development, innovation workshops and experiential programmes." },
  { icon: RocketLaunch, t: "Incubation Centres", d: "Structured mentoring for student founders and early-stage ventures." },
];

const FORMATS = [
  { t: "One-Day Immersion", i: Lightning },
  { t: "Multi-Session Programme", i: Chats },
  { t: "Semester-Linked Programme", i: Calendar },
  { t: "Bootcamp", i: Target },
  { t: "Incubation Mentorship", i: ChalkboardTeacher },
  { t: "Custom Institutional Programme", i: Buildings },
];

function JourneyRibbon({ steps }) {
  return (
    <div className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-3">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy"
          >
            {s}
          </motion.span>
          {i < steps.length - 1 && <ArrowRight size={14} weight="bold" className="text-gold" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Institutions() {
  const [showCaps, setShowCaps] = useState(false);

  return (
    <div data-testid="institutions-page">
      {/* HERO */}
      <section className="bg-cloud pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Founder+ for Institutions</Eyebrow>
            <h1 className="mt-7 max-w-4xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.4rem]">
              Preparing students to{" "}
              <span className="font-semibold text-gold">Build. Work. And Lead.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              Founder+ works with colleges, universities and incubation centres to prepare
              students for the world beyond the classroom — through entrepreneurship
              development, corporate readiness and startup mentorship.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <PrimaryButton to="/contact" testid="inst-primary-cta">
                Partner With Founder+ <ArrowRight size={17} weight="bold" />
              </PrimaryButton>
              <a
                href="#build"
                data-testid="inst-secondary-cta"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold"
              >
                Explore Our Programmes
                <ArrowRight size={17} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THREE PATHWAYS */}
      <section className="bg-white py-24 lg:py-32" data-testid="pathways-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Institutional Pathways</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              One institution. <span className="text-gold">Multiple student journeys.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {PATHWAYS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal
                  key={p.tag}
                  delay={i * 0.08}
                  data-testid={`pathway-${p.tag.toLowerCase()}`}
                >
                  <a
                    href={`#${p.anchor}`}
                    className="group flex h-full flex-col rounded-3xl border border-slate-100 bg-cloud/40 p-9 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
                  >
                    <div className="flex items-center gap-3">
                      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-teal/10 text-teal">
                        <Icon size={26} weight="duotone" />
                      </span>
                      <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">{p.tag}</span>
                    </div>
                    <h3 className="mt-6 font-heading text-2xl font-semibold text-navy">{p.title}</h3>
                    <p className="mt-4 flex-1 leading-relaxed text-slate-600">{p.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors group-hover:text-gold">
                      Learn more <ArrowRight size={15} weight="bold" />
                    </span>
                  </a>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUILD — 360° ENTREPRENEURSHIP */}
      <section id="build" className="bg-cloud py-24 lg:py-32" data-testid="build-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">Build · Pathway 01</span>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              360° Entrepreneurship Development
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-slate-500">
              From entrepreneurial mindset to venture creation.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-8">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal">The Journey</p>
              <JourneyRibbon steps={BUILD_JOURNEY} />
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <p className="mt-14 max-w-3xl font-heading text-2xl font-light leading-snug text-navy sm:text-[1.75rem]">
              Entrepreneurship is not just about starting a company. It is about developing
              the capability to <span className="text-gold">identify opportunities, create
              value and build something meaningful.</span>
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {BUILD_DOMAINS.map((d, i) => (
              <Reveal
                key={d.n}
                delay={(i % 2) * 0.06}
                data-testid={`build-domain-${d.n}`}
                className="group flex gap-6 rounded-2xl border border-slate-100 bg-white p-7 transition-colors duration-300 hover:border-gold/40"
              >
                <div className="font-heading text-2xl font-bold text-gold shrink-0">{d.n}</div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-navy">{d.t}</h3>
                  <p className="mt-2 leading-relaxed text-slate-500">{d.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORK — CORPORATE READINESS LAB */}
      <section id="work" className="bg-white py-24 lg:py-32" data-testid="work-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">Work · Pathway 02</span>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Corporate Readiness Lab
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-slate-500">
              Preparing students not just to get their first job — but to navigate it well.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {CRL_LEVELS.map((l, i) => (
              <Reveal
                key={l.n}
                delay={(i % 2) * 0.06}
                data-testid={`crl-level-${l.n}`}
                className="rounded-3xl border border-slate-100 bg-cloud/50 p-8"
              >
                <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-teal">
                  <span>Level {l.n}</span>
                </div>
                <h3 className="mt-4 font-heading text-2xl font-semibold text-navy">{l.t}</h3>
                <p className="mt-4 font-heading text-lg italic text-gold">{l.q}</p>
                <p className="mt-3 leading-relaxed text-slate-600">{l.d}</p>
              </Reveal>
            ))}
          </div>

          {/* REAL METHODOLOGY */}
          <Reveal className="mt-20 rounded-[28px] bg-navy p-10 text-white lg:p-14" data-testid="real-methodology">
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">Methodology</span>
            <h3 className="mt-4 font-heading text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              REAL<sup className="text-gold">™</sup> Learning Methodology
            </h3>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {REAL_METHOD.map((r) => (
                <div key={r.l} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <div className="font-heading text-5xl font-bold text-gold">{r.l}</div>
                  <h4 className="mt-4 font-heading text-lg font-semibold">{r.w}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{r.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">The Learning Flow</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-3">
                {REAL_FLOW.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white">
                      {s}
                    </span>
                    {i < REAL_FLOW.length - 1 && <ArrowRight size={14} weight="bold" className="text-gold" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <p className="mt-10 max-w-2xl font-heading text-xl font-light leading-snug">
              We don't just tell students about the workplace.{" "}
              <span className="text-gold">We let them experience it before they enter it.</span>
            </p>
          </Reveal>

          {/* 22 CAPABILITIES */}
          <Reveal className="mt-16" data-testid="crl-capabilities">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.24em] text-teal">Curriculum</span>
                <h3 className="mt-3 font-heading text-2xl font-semibold text-navy sm:text-3xl">
                  Built around a progressive 22-capability curriculum.
                </h3>
              </div>
              <button
                onClick={() => setShowCaps((v) => !v)}
                data-testid="toggle-capabilities"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
              >
                {showCaps ? "Hide" : "Show"} full curriculum
                <CaretDown size={14} weight="bold" className={`transition-transform ${showCaps ? "rotate-180" : ""}`} />
              </button>
            </div>
            {showCaps && (
              <motion.ol
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.4 }}
                className="mt-8 grid list-decimal gap-x-8 gap-y-3 rounded-2xl border border-slate-100 bg-cloud/50 p-8 pl-14 sm:grid-cols-2"
              >
                {CRL_CAPABILITIES.map((c) => (
                  <li key={c} className="text-[15px] text-slate-700 marker:text-gold marker:font-bold">
                    {c}
                  </li>
                ))}
              </motion.ol>
            )}
          </Reveal>
        </div>
      </section>

      {/* VENTURE — MENTORSHIP */}
      <section id="venture" className="bg-cloud py-24 lg:py-32" data-testid="venture-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">Venture · Pathway 03</span>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Startup Mentorship & Incubation Support
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-slate-500">
              From student founder to startup builder.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-8">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal">The Mentoring Journey</p>
              <JourneyRibbon steps={VENTURE_JOURNEY} />
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <h3 className="mt-14 font-heading text-xl font-semibold text-navy">Key mentoring areas</h3>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {VENTURE_AREAS.map((a) => (
                <div
                  key={a}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 text-[15px] text-slate-700"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  {a}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <p className="mt-10 max-w-2xl font-heading text-xl font-light leading-snug text-navy">
              Structured business mentoring for student founders and early-stage ventures.{" "}
              <span className="text-gold">Not motivational mentoring.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="bg-white py-24 lg:py-32" data-testid="audience-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Built for Institutions</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Who we work with.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCE.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal
                  key={a.t}
                  delay={(i % 4) * 0.06}
                  className="rounded-3xl border border-slate-100 bg-cloud/50 p-7 transition-all duration-500 hover:-translate-y-1"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal/10 text-teal">
                    <Icon size={22} weight="duotone" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-semibold text-navy">{a.t}</h3>
                  <p className="mt-2 leading-relaxed text-slate-500">{a.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROGRAMME FORMATS */}
      <section className="bg-cloud py-24 lg:py-32" data-testid="formats-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Programme Formats</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Designed around your institution.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FORMATS.map((f, i) => {
              const Icon = f.i;
              return (
                <Reveal
                  key={f.t}
                  delay={(i % 3) * 0.06}
                  className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-6"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold/12 text-gold">
                    <Icon size={20} weight="duotone" />
                  </span>
                  <p className="font-heading text-lg font-semibold text-navy">{f.t}</p>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <p className="mt-10 max-w-3xl text-[15px] leading-relaxed text-slate-500">
              Programmes can be customised around student profile, discipline, academic
              calendar, placement objectives and institutional priorities.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-navy" data-testid="inst-cta-band">
        <img src="/images/cta.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-[1320px] px-6 py-24 text-center lg:px-10 lg:py-28">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-heading text-4xl font-light leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Preparing students for{" "}
              <span className="font-semibold text-gold">what comes next.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-white/70">
              Whether an institution wants to build entrepreneurial capability, prepare
              students for professional life or strengthen its startup ecosystem, Founder+
              can design a programme around its requirements.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <PrimaryButton to="/contact" testid="inst-cta-primary" className="bg-gold text-navy hover:bg-gold-light">
                Partner With Founder+
              </PrimaryButton>
              <a
                href="mailto:foun.plus@gmail.com"
                data-testid="inst-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-navy"
              >
                Discuss Your Institution's Requirements
                <ArrowRight size={16} weight="bold" />
              </a>
            </div>
            <p className="mt-6 text-sm text-white/50">foun.plus@gmail.com</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
